import { StateCreator } from 'zustand'
import { InviteLinksState, initialState } from '../state'
import {
    DetalizationOptionType,
    UsersSortOptionType,
    typedUsersSortOptions,
    typedDetalizationSortOptions,
    InviteLinksChartOptionType,
    SubscriberStatusSortOptionType,
    typedSubscriberStatusSortOptions,
    LineDateSortOptionType,
} from '../../constants'
import { UserPermissionsResponse } from '@synapex/shared'
import {
    SelectedLinksByGroupType,
    SelectedSiteLinksByGroupType,
} from '../types'
import { InviteLinkWithAnalyticsType } from '@/shared/api'
import { SiteLinkWithAnalyticsType } from '@/shared/api/site-links-group/site-links-group.types'

export interface InviteLinksFeatureSlice {
    chartIsExpanded: boolean
    selectedChartMetric: InviteLinksChartOptionType
    detalizationSortInvite: DetalizationOptionType
    usersSortInvite: UsersSortOptionType
    subscriberStatusSortInvite: SubscriberStatusSortOptionType
    startDateInvite: string
    endDateInvite: string
    lineDateInvite: LineDateSortOptionType | null
    selectedLinksInviteByGroup: SelectedLinksByGroupType
    selectedSiteLinksByGroup: SelectedSiteLinksByGroupType
    toggleChartIsExpanded: () => void
    setSelectedChartMetric: (value: InviteLinksChartOptionType) => void
    setLineDateInvite: (value: LineDateSortOptionType | null) => void
    setStartDateInvite: (value: string) => void
    setEndDateInvite: (value: string) => void
    setDetalizationSortInvite: (value: DetalizationOptionType) => void
    setUsersSortInvite: (value: UsersSortOptionType) => void
    setSubscriberStatusSortInvite: (
        value: SubscriberStatusSortOptionType
    ) => void
    toggleSelectedLinkInviteByGroup: (
        groupId: number,
        link: InviteLinkWithAnalyticsType
    ) => void
    toggleSelectedLinkSiteByGroup: (
        groupId: number,
        link: SiteLinkWithAnalyticsType
    ) => void
    clearWithoutPermissionSelectedLinksInviteByChannel: (
        channelId: number,
        userId: number,
        permission: UserPermissionsResponse
    ) => void
    resetInviteLinks: () => void
    clearSelectedLinksInviteByGroup: (groupId: number) => void
    clearSelectedSiteLinksByGroup: (groupId: number) => void
}

export const createInviteLinksFeatureSlice: StateCreator<
    InviteLinksState,
    [],
    [],
    InviteLinksFeatureSlice
> = (set) => ({
    chartIsExpanded: false,
    detalizationSortInvite: typedDetalizationSortOptions[0],
    usersSortInvite: typedUsersSortOptions[0],
    subscriberStatusSortInvite: typedSubscriberStatusSortOptions[0],
    selectedLinksInviteByGroup: {},
    selectedSiteLinksByGroup: {},
    startDateInvite: '',
    endDateInvite: '',
    lineDateInvite: null,
    selectedChartMetric: { value: 'subscribedCount', label: 'Вступлений' },

    toggleChartIsExpanded: () =>
        set((state) => ({ chartIsExpanded: !state.chartIsExpanded })),
    setSelectedChartMetric: (value) => set({ selectedChartMetric: value }),
    setLineDateInvite: (value) => set({ lineDateInvite: value }),
    setStartDateInvite: (value) => set({ startDateInvite: value }),
    setEndDateInvite: (value) => set({ endDateInvite: value }),
    setDetalizationSortInvite: (value) =>
        set({ detalizationSortInvite: value }),
    setUsersSortInvite: (value) => set({ usersSortInvite: value }),
    setSubscriberStatusSortInvite: (value) =>
        set({ subscriberStatusSortInvite: value }),

    toggleSelectedLinkInviteByGroup: (groupId, link) => {
        set((state) => {
            const currentSelected =
                state.selectedLinksInviteByGroup[groupId] || []
            const linkIndex = currentSelected.findIndex(
                (selectedLink) =>
                    selectedLink.invite_link_id === link.invite_link_id
            )
            let newSelected

            if (linkIndex === -1) {
                newSelected = [...currentSelected, link]
            } else {
                newSelected = currentSelected.filter(
                    (_, index) => index !== linkIndex
                )
            }

            return {
                selectedLinksInviteByGroup: {
                    ...state.selectedLinksInviteByGroup,
                    [groupId]: newSelected,
                },
            }
        })
    },

    toggleSelectedLinkSiteByGroup: (groupId, link) => {
        set((state) => {
            const currentSelected =
                state.selectedSiteLinksByGroup[groupId] || []
            const linkIndex = currentSelected.findIndex(
                (selectedLink) =>
                    selectedLink.site_link_id === link.site_link_id
            )
            let newSelected

            if (linkIndex === -1) {
                newSelected = [...currentSelected, link]
            } else {
                newSelected = currentSelected.filter(
                    (_, index) => index !== linkIndex
                )
            }

            return {
                selectedSiteLinksByGroup: {
                    ...state.selectedSiteLinksByGroup,
                    [groupId]: newSelected,
                },
            }
        })
    },

    clearSelectedLinksInviteByGroup: (groupId: number) => {
        set((state) => ({
            selectedLinksInviteByGroup: {
                ...state.selectedLinksInviteByGroup,
                [groupId]: [],
            },
        }))
    },

    clearSelectedSiteLinksByGroup: (groupId: number) => {
        set((state) => ({
            selectedSiteLinksByGroup: {
                ...state.selectedSiteLinksByGroup,
                [groupId]: [],
            },
        }))
    },

    clearWithoutPermissionSelectedLinksInviteByChannel: (
        channelId: number,
        userId: number,
        permission: UserPermissionsResponse | null
    ) => {
        set((state) => {
            if (!permission) {
                return {}
            }

            if (!permission.invite_link) {
                const currentSelected =
                    state.selectedLinksInviteByGroup[channelId] || []
                const filteredLinks = currentSelected.filter(
                    (link) => link.user_id === userId
                )

                return {
                    selectedLinksInviteByGroup: {
                        ...state.selectedLinksInviteByGroup,
                        [channelId]: filteredLinks,
                    },
                }
            }

            return {}
        })
    },
    resetInviteLinks: () => {
        set({ ...initialState })
    },
})
